import React, {useContext, useEffect, useMemo} from "react";
import {ConfigProvider, Form, Modal, Select} from "antd";
import {useForm} from "antd/es/form/Form";
import * as globalStyles from "../App.module.css";
import AiExtractor from "../../domain/AiExtractor";
import {buildGroupedAiExtractorOptions, renderAiExtractorOption} from "./AiExtractorSelectOptions";
import {AiProviderCommunicationLoggingMode} from "../../service/VulSourceItemService";
import {AppContextContext} from "../../Contexts";

type Props = {
    open: boolean;
    aiExtractors: AiExtractor[];
    initialAiExtractorId?: string;
    showProviderCommunicationLogOption?: boolean;
    onOk: (aiExtractorId: string, providerCommunicationLoggingMode: AiProviderCommunicationLoggingMode) => void;
    onCancel: () => void;
};

type FormValues = {
    aiExtractorId?: string;
    providerCommunicationLoggingMode?: AiProviderCommunicationLoggingMode;
};

function AiExtractorSelectModal(props: Props) {
    const appContext = useContext(AppContextContext);
    const [form] = useForm<FormValues>();
    const groupedOptions = useMemo(
        () => buildGroupedAiExtractorOptions(props.aiExtractors, appContext.user.isSystemAdmin()),
        [props.aiExtractors, appContext.user]
    );

    useEffect(() => {
        if (!props.open) {
            return;
        }

        const initialId = props.initialAiExtractorId
        && props.aiExtractors.some(aiExtractor => aiExtractor.id === props.initialAiExtractorId)
            ? props.initialAiExtractorId
            : undefined;

        form.setFieldsValue({
            aiExtractorId: initialId,
            providerCommunicationLoggingMode: "DEFAULT"
        });
    }, [props.open, props.initialAiExtractorId, props.aiExtractors, form]);

    return (
        <Modal
            open={props.open}
            title={"Select AI extractor"}
            width={900}
            okText={"OK"}
            cancelText={"Cancel"}
            destroyOnHidden={true}
            okButtonProps={{disabled: props.aiExtractors.length === 0}}
            mask={{closable: false}}
            onOk={() => {
                form.validateFields().then(values => {
                    props.onOk(values.aiExtractorId!, values.providerCommunicationLoggingMode ?? "DEFAULT");
                    form.resetFields();
                });
            }}
            onCancel={() => {
                props.onCancel();
                form.resetFields();
            }}
        >
            <ConfigProvider theme={{components: {Form: {itemMarginBottom: 8}}}}>
                <Form form={form} className={globalStyles["common__form"]} labelCol={{span: 5}}>
                    <Form.Item
                        name={"aiExtractorId"}
                        label={"AI extractor"}
                        rules={[{required: true, message: "AI extractor is required."}]}
                    >
                        <Select
                            showSearch={true}
                            options={groupedOptions}
                            optionFilterProp="label"
                            optionLabelProp="label"
                            optionRender={option => {
                                const aiExtractor = (option.data as { aiExtractor?: AiExtractor }).aiExtractor;

                                return aiExtractor ? renderAiExtractorOption(aiExtractor) : option.label;
                            }}
                        />
                    </Form.Item>
                    {props.showProviderCommunicationLogOption && (
                        <Form.Item
                            name={"providerCommunicationLoggingMode"}
                            label={"Log AI provider calls"}
                        >
                            <Select
                                options={[
                                    {value: "DEFAULT", label: "use extractor setting"},
                                    {value: "ENABLED", label: "log for this run"},
                                    {value: "DISABLED", label: "don't log for this run"}
                                ]}
                            />
                        </Form.Item>
                    )}
                </Form>
            </ConfigProvider>
        </Modal>
    );
}

export default AiExtractorSelectModal;
